import { Box, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { TimePlanningBalance, TimePlanningBalancesByPeriod } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { InjectHoverProvider } from 'modules/core/lib/hoverContext'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

import EmployeeCellContent from './EmployeeCellContent'
import EmployeeInfoCell from './EmployeeInfoCell'
import usePlannedHours from './usePlannedHours'

interface Props {
  shiftsRow: ShiftsRow
  balances?: TimePlanningBalance
  periodBalances?: TimePlanningBalancesByPeriod[]
}

const EmployeeCell = ({ shiftsRow, balances, periodBalances }: Props) => {
  const [open, toggle] = useToggle(false)
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasBankOfHoursEnabled = hasFeatureFlag('dev_time_planning_balances_in_shifts')
  const hasOverPlannedAlertEnabled = hasFeatureFlag('dev_shift_management_over_planned_alert')
  const { color, isOverMaximum, plannedHoursTooltipCopy } = usePlannedHours(
    shiftsRow,
    balances,
    periodBalances
  )
  const [{ canManageShifts }] = usePermissions()
  const showOverPlannedAlert = canManageShifts && hasOverPlannedAlertEnabled

  if (hasBankOfHoursEnabled)
    return (
      <InjectHoverProvider>
        <Box background="grey000White" flexDirection="row" height="full">
          {showOverPlannedAlert && (
            <Tooltip.Alt
              attachment="top center"
              targetAttachment="bottom center"
              open={open}
              toggle={toggle}
              label={plannedHoursTooltipCopy}
            >
              <Box background={color} width={open ? 's6' : 's4'} height="full" />
            </Tooltip.Alt>
          )}
          <Box padding="s8">
            <EmployeeInfoCell
              shiftsRow={shiftsRow}
              balances={balances}
              isOverMaximum={isOverMaximum}
              plannedHoursTooltipCopy={plannedHoursTooltipCopy}
            />
          </Box>
        </Box>
      </InjectHoverProvider>
    )

  // TODO: To remove this and the related components in favour of the ones above
  // once Bank of Hours is released
  return (
    <InjectHoverProvider>
      <Box
        background="grey000White"
        flexDirection="row"
        justifyContent="spaceBetween"
        height="full"
        padding="s8"
        gap="s8"
      >
        <EmployeeCellContent shiftsRow={shiftsRow} />
      </Box>
    </InjectHoverProvider>
  )
}

export default observer(EmployeeCell)
